import { useReducer } from 'react';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';

import { TextFieldProps } from '@mui/material/TextField';
import TextField from '@mui/material/TextField';

// ----------------------------------------------------------------------

interface RHFTextFieldProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<TextFieldProps, 'name'> {
  name: Path<TFieldValues>;
  helperText?: string;
  type?: string;
  /** When true, hide the validation helper text but keep the error visual state */
  hideErrorMessage?: boolean;
  /**
   * `type="number"` only. Opt in to distinguishing an empty box from zero: an emptied field
   * commits `undefined` instead of `Number('') === 0`, so a schema can reject it (`.required()`)
   * the way the Formik screens did, and a stored `0` renders as `0` instead of blank.
   *
   * Off by default: the wrapper's historical behaviour — clearing commits `0`, and `0` is shown
   * blank so the user does not have to delete it before typing — is what every existing consumer
   * is written against. Turn it on where an empty value is a distinct, invalid state.
   */
  emptyAsUndefined?: boolean;
}

/**
 * A numeric box shows `''` for the value it must not display: under the default semantics that is
 * `0` (which the wrapper treats as "nothing typed yet"), and under `emptyAsUndefined` it is the
 * absence of a value, so a real `0` is rendered.
 */
const renderedValue = (
  type: string | undefined,
  value: unknown,
  emptyAsUndefined: boolean
): unknown => {
  if (type !== 'number') return value;
  if (emptyAsUndefined) return value ?? '';
  return value === 0 ? '' : value;
};

export function RHFTextField<TFieldValues extends FieldValues = FieldValues>({
  name,
  helperText,
  type,
  hideErrorMessage = false,
  emptyAsUndefined = false,
  onBlur,
  ...other
}: RHFTextFieldProps<TFieldValues>) {
  const { control, getValues } = useFormContext<TFieldValues>();
  // RHF hands an `undefined` form value back as the field's default, so an emptied box reads the
  // committed value from the form store instead (it survives a remount), and re-renders on each
  // change because emptying it may not change the watched value at all.
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  const readsStore = type === 'number' && emptyAsUndefined;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          error={!!error}
          helperText={hideErrorMessage ? helperText ?? '' : error?.message ?? helperText}
          type={type}
          value={renderedValue(type, readsStore ? getValues(name) : field.value, emptyAsUndefined)}
          onBlur={(e) => {
            field.onBlur();
            onBlur?.(e);
          }}
          onChange={(event) => {
            if (type !== 'number') {
              field.onChange(event.target.value);
              return;
            }
            const raw = event.target.value;
            field.onChange(emptyAsUndefined && raw === '' ? undefined : Number(raw));
            if (readsStore) rerender();
          }}
          {...other}
        />
      )}
    />
  );
}
